import { INSIGHTS_KEY, MIGRATION_DEFAULT_DESTINATION, PISTIL_NEXT_KEY } from '../constants/migration';
import { getCookieInDocument } from './tools';

const hasInsightsRole = (roles: string[] | string | undefined | null): boolean =>
  Array.isArray(roles) ? roles.includes('PistilInsights') : roles === 'PistilInsights';

/**
 * Decide whether a user landing on /welcome without an explicit per-navigation
 * destination (sessionStorage) should be AUTO-redirected to Pistil Next.
 *
 * Rule (Phase 1): only if the user has Insights AND already chose Pistil Next
 * before (persisted preference). Choosing Insights overwrites the preference, so
 * a user who hasn't migrated always sees the chooser.
 *
 * Phase 2 (default-to-new): with MIGRATION_DEFAULT_DESTINATION === PISTIL_NEXT_KEY,
 * it also auto-redirects when there is NO prior preference (opt-out instead of opt-in).
 *
 * @returns PISTIL_NEXT_KEY if it should auto-redirect; null if it should show the chooser.
 */
export const resolveStickyDestination = (
  roles: string[] | string | undefined | null,
  persistedDestination: string | null,
  defaultDestination: string | null = MIGRATION_DEFAULT_DESTINATION,
): string | null => {
  if (!hasInsightsRole(roles)) return null;
  if (persistedDestination === PISTIL_NEXT_KEY) return PISTIL_NEXT_KEY;
  if (!persistedDestination && defaultDestination === PISTIL_NEXT_KEY) return PISTIL_NEXT_KEY;
  return null;
};

/**
 * Read the destination preference persisted in the shared `destinationApp` cookie
 * (`.pistildata.com`). The value is stored as base64 (cookifyString); a plain value
 * is tolerated just in case. `null` when there is no preference.
 */
export const getPersistedDestinationApp = (): string | null => {
  const raw = getCookieInDocument('destinationApp');
  if (!raw) return null;
  try {
    return window.atob(raw);
  } catch {
    return raw;
  }
};

/**
 * Destinations another app can navigate the gateway to (`/<destination>` or
 * `?destinationApp=<destination>`). When one of these is present, the
 * `switch (destinationApp)` in welcome.tsx owns the routing decision.
 *
 * `landing` / `welcome` / `time-out` are gateway-local: no origin app asked
 * for them, so the role-based fallbacks decide instead.
 */
export const isExplicitAppDestination = (destinationApp: string | null | undefined): boolean =>
  destinationApp === INSIGHTS_KEY ||
  destinationApp === PISTIL_NEXT_KEY ||
  destinationApp === 'vmiq' ||
  destinationApp === 'legacy_insights';

/**
 * Whether welcome.tsx's LEGACY single-role fallback (`typeof roles === 'string'`
 * → redirect by role) should run.
 *
 * It must NOT run once the `switch (destinationApp)` has already routed an explicit
 * destination: `break` exits the switch, not the effect, so the fallback used to
 * fire on top of it — rewriting the `destinationApp` cookie to 'insights' and
 * scheduling a second `window.location.assign`. Both assigns are
 * `setTimeout(…, 0)`, so the later Insights one won and "Go to Pistil Next"
 * bounced straight back to Insights for every user whose OIDC `role` claim is a
 * single string. Users with several roles (array claim) were never affected.
 */
export const shouldApplyLegacySingleRoleRedirect = (
  destinationApp: string | null | undefined,
  roles: string[] | string | undefined | null,
): boolean => typeof roles === 'string' && !isExplicitAppDestination(destinationApp);
